import React, { useEffect, useState } from 'react';
import LoadingIndicator from '../LoadingIndicator';
import useTrackEvent from '../../utils/trackEvent';
import type { Question, Answer } from '../../types/questionnaire';

interface Props {
  themes: Question;
  onSubmit: () => void;
  selectedThemes: Answer[];
  wrappedThemes: Answer[];
  isLoading: boolean;
  error: string | null;
}

const Submission = ({ themes, onSubmit, selectedThemes, wrappedThemes, isLoading, error }: Props) => {
  const trackEvent = useTrackEvent();
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedSubThemes, setSelectedSubThemes] = useState<Record<string, unknown>[]>([]);
  const [selectedSubSubThemes, setSelectedSubSubThemes] = useState<Record<string, unknown>[]>([]);

  const sendSegmentEvents = () => {
    try {
      selectedSubSubThemes.forEach((subsubtheme) => { trackEvent('Select_Case', subsubtheme); });
      selectedSubThemes.forEach((subtheme) => { trackEvent('Select_Subtheme', subtheme); });
    } catch (err) {
      console.log('not all events were sent', err);
    }
  };

  useEffect(() => {
    const selectedOptions =
      themes.answers.some((theme) => theme.checked) &&
      themes.answers.some((theme) => theme.questions?.[0]?.answers.some((answer) => answer.checked));
    setIsDisabled(!selectedOptions);
    try {
      const getFilteredAnswers = (themes: Question) => {
        return themes.answers
          .filter((theme) => theme.checked)
          .flatMap((theme) => theme.questions?.[0]?.answers ?? [])
          .filter((answer) => answer.checked)
          .map(({ image: _i, content: _c, pill: _p, description: _d, ...rest }) => rest);
      };

      type FilteredAnswer = Omit<Answer, 'image' | 'content' | 'pill' | 'description'>;
      const filteredAnswers = getFilteredAnswers(themes) as FilteredAnswer[];

      const createSelectedThemes = (filteredAnswers: FilteredAnswer[]) => {
        const themeGroups = filteredAnswers.reduce<Record<string, FilteredAnswer[]>>((acc, answer) => {
          const key = answer.theme ?? '';
          if (!acc[key]) acc[key] = [];
          acc[key].push(answer);
          return acc;
        }, {});

        return Object.entries(themeGroups).map(([theme, answers]) => ({
          theme,
          subcases: answers.map((answer) => {
            const onlyCheckedAnswers = answer.questions?.[0]?.answers.filter((a) => a.checked) ?? [];
            return {
              questions: [{
                select: onlyCheckedAnswers.length > 0 ? (onlyCheckedAnswers[0]?.description || onlyCheckedAnswers[0]?.title) : '',
                theme: answer.theme,
                subTheme: answer.title,
              }],
            };
          }),
        }));
      };

      const computedThemes = createSelectedThemes(filteredAnswers);
      const subThemes = filteredAnswers.map((answer) => ({ theme: answer.theme, select: answer.title }));
      const subsubThemes = computedThemes.flatMap((theme) =>
        theme.subcases.flatMap((subcase) => subcase.questions.map((question) => question))
      );
      setSelectedSubThemes(subThemes as Record<string, unknown>[]);
      setSelectedSubSubThemes(subsubThemes as Record<string, unknown>[]);
    } catch (err) {
      console.error('An error occurred while sending events to segment:', err);
    }
  }, [themes]);

  const scrollToTop = () => { window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }); };

  if (!selectedThemes.length) return null;

  return (
    <div className="flex flex-col w-full gap-5 lg:flex-row lg:gap-6">
      <div className="flex-none h-fit w-full lg:max-w-[171px] flex flex-col lg:flex-row lg:gap-3 items-center lg:mt-6">
        <span className="w-full flex-1 bg-white rounded border-2 py-2 px-3 font-bold text-center lg:text-left border-secondary-orange">
          Afsluiting
        </span>
        <div className="lg:hidden arrow-down border-t-[16px] border-secondary-orange"></div>
        <span className="hidden z-10 lg:block h-5 w-5 bg-secondary-orange rounded-full"></span>
      </div>
      <div className="flex-1 lg:inline-flex">
        <div className="hidden lg:block arrow-left lg:mt-6"></div>
        <div className="flex-1 rounded-lg p-5 bg-white lg:rounded-[20px] lg:p-10">
          <h4>Afsluiting</h4>
          <div className="text-lg mt-3">
            {wrappedThemes.length ? (
              <>
                <p>Als we het goed begrijpen dan kan jij hulp gebruiken bij:</p>
                <ul className="list-disc pl-5">
                  {wrappedThemes.map((theme) =>
                    theme.questions?.[0]?.answers
                      .filter((answer) => answer.checked)
                      .map((answer) => {
                        if (answer.questions?.[0]?.answers?.some((a) => a.checked)) {
                          const selectedItems = answer.questions[0].answers.filter((a) => a.checked);
                          return selectedItems.map((selectedItem) => {
                            if (selectedItem.questions?.[0]?.answers?.some((a) => a.checked)) {
                              return selectedItem.questions.map((selectedSubItem) => {
                                return selectedSubItem.answers.map((selectedSubSubItem) => {
                                  if (!selectedSubSubItem.checked) return null;
                                  return (
                                    <li key={selectedSubSubItem.id}>
                                      {answer.title}: {selectedSubSubItem.result}
                                    </li>
                                  );
                                });
                              });
                            }
                            return (
                              <li key={selectedItem.id}>
                                {answer.title}: {selectedItem.result}
                              </li>
                            );
                          });
                        }
                        return <li key={answer.id}>{answer.title}</li>;
                      })
                  )}
                </ul>
              </>
            ) : (
              <p className="text-lg">Je hebt nog niets geselecteerd waar je hulp bij wil.</p>
            )}
          </div>
          {error && <p className="text-red-500">{error}</p>}
          <div className="flex flex-wrap gap-5 mt-5">
            <button
              className="w-full sm:w-fit btn btn-primary"
              onClick={() => { onSubmit(); sendSegmentEvents(); }}
              disabled={isLoading || isDisabled}
            >
              {isLoading ? <LoadingIndicator /> : null}
              Dat klopt, geef mij advies
            </button>
            <button
              className="w-full sm:w-fit btn btn-primary-outline"
              onClick={scrollToTop}
              disabled={isLoading || isDisabled}
            >
              Nee, ik wil nog wat toevoegen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Submission;
